/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {Preference} from '@ion/core/src/types/preferences';
import {mapViewGoToOptions} from '@ion/dls/src/maps/constants';
import {useBasemap} from '@ion/dls/src/maps/hooks/useBasemap';
import {useViewExtent} from '@ion/dls/src/maps/hooks/useViewExtent';
import {
  CURRENT_LAYER
} from '@ion/dls/src/maps/layers/CurrentLayerSettings';
import {
  BaseMapEnum,
  SavedMapViewType
} from '@ion/dls/src/types/types';
import {
  setSavedMapViews,
  setTrackingBaseMap,
  toggleMapControls,
  updateGeofenceComponents
} from '@/store/actions/tracking';
import {filterSavedMapViews} from '@/store/helpers';
import {
  mapBasemapSelector,
  mapLayerSelector,
  mobileMapControlsOpenSelector,
  operationalLayersSelector,
  savedMapViewsSelector,
  selectedToolSelector,
  toolDetailViewSelector
} from '@/store/selectors/tracking-selectors';
import useLayerChooserProps from './useLayerChooserProps';

/**
 * The useBottomSheetProps hook is used for sharing props to Bottom Sheet
 * 
 * ```typescript
 * 
 *   import useBottomSheetProps from './hooks/useBottomSheetProps';
 * 
 *   const bottomSheetProps = useBottomSheetProps();
 * 
 *   // bottomLeftToolProps
 * 
 * ```
 */
export const useBottomSheetProps = () => {
  const dispatch              = useDispatch();
  const isMounted             = useHasMounted(null);
  const selectedTool          = useSelector(selectedToolSelector);
  const toolDetailView        = useSelector(toolDetailViewSelector);
  const mobileMapControlsOpen = useSelector(mobileMapControlsOpenSelector);
  const basemap               = useSelector(mapBasemapSelector);
  const layers                = useSelector(mapLayerSelector);
  const operationalLayers     = useSelector(operationalLayersSelector);
  const savedMapViews         = useSelector(savedMapViewsSelector);
  const {updateMapLayers}     = useLayerChooserProps();
  const {
    initializeViewExtent,
    getInitialExtent,
    createExtent
  } = useViewExtent();
  const {
    loadBasemap,
    currentBasemap,
    createOSMBasemap,
    getCurrentBasemap
  } = useBasemap();

  useEffect(() => {
    if (isMounted) {
      loadBasemap(basemap, true);
      initializeViewExtent(true);
    }
  }, [isMounted, basemap]);

  const onGoToHomeView = useCallback((map: __esri.Map, mapView: __esri.MapView) => {
    const extent = getInitialExtent({
      map,
      assetsExtent : null
    });
    extent && mapView?.goTo(extent, mapViewGoToOptions);
  }, []);

  const onBaseMapChange = useCallback((basemap: BaseMapEnum) => {
    dispatch(setTrackingBaseMap(basemap));
  }, []);

  const setSavedMapView = useCallback((mapView: __esri.MapView, savedMapView, applyExtent = true) => {
    if (savedMapView) {
      const {
        extent,
        basemap,
        layers: savedLayers,
        operationalLayers: savedOperationalLayers
      } = savedMapView;
      if (applyExtent) {
        const savedMapViewExtent = createExtent(extent);
        mapView?.goTo(savedMapViewExtent, mapViewGoToOptions);
        mapView.extent      = savedMapViewExtent;
        mapView.map.basemap = getCurrentBasemap(basemap);
      }
      onBaseMapChange(basemap);
      if (updateMapLayers && (savedLayers || savedOperationalLayers)) {
        updateMapLayers({
          isAppendLayers : false,
          savedOperationalLayers,
          savedLayers
        });
      }
    }
  }, [mapViewGoToOptions, getCurrentBasemap, updateMapLayers]);

  const closeMapControlsSheet = useCallback(function() {
    dispatch(toggleMapControls(false));
  }, []);

  const onMapViewClick = useCallback(function() {
    const isGrayVector = basemap === BaseMapEnum.grayVector;
    const newBasemap = isGrayVector ? BaseMapEnum.hybrid : BaseMapEnum.grayVector;
    onBaseMapChange(newBasemap);
  }, [basemap]);

  // opens the geoFence filters from the map geoFence panel
  const onViewAllClick = useCallback(function() {
    dispatch(updateGeofenceComponents(true, false) as any);
  }, []);

  const getVisibleOperationalLayer = useCallback((operationalLayers) => {
    const visibleOperationalLayers = (
      operationalLayers
        ?.filter(({visibility} = {}) => visibility)
        ?.map(({id, layers, opacity} = {}) => ({
          id,
          layers,
          opacity
        }))) || [];
    return visibleOperationalLayers;
  }, []);

  const onSaveMapView = useCallback((mapView: SavedMapViewType) => {
    const enabledLayers = [...(layers || [])];
    if (layers.some(layer => layer === CURRENT_LAYER)) {
      enabledLayers.push(CURRENT_LAYER);
    }
    const savedMapView = {
      ...mapView,
      layers : {
        enabledLayers
      },
      operationalLayers : getVisibleOperationalLayer(operationalLayers)
    };
    const updatedMapViews = savedMapViews?.length ? [...savedMapViews, savedMapView] : [savedMapView];
    UserPreferences.set(Preference.savedMapViews, updatedMapViews, true);
    dispatch(setSavedMapViews(updatedMapViews));
  }, [layers, operationalLayers, savedMapViews]);

  const editSavedMapView = useCallback((value, mapView) => {
    const trackingMapLayers = UserPreferences.get(Preference.trackingMapLayers);
    const {enabledLayers}   = trackingMapLayers;
    if (layers.some(layer => layer === CURRENT_LAYER)) {
      enabledLayers.push(CURRENT_LAYER);
    }
    const updatedMapViews = savedMapViews.map(savedMapView => {
      if (savedMapView.id !== mapView.id) {
        return savedMapView;
      } else {
        const updatedMapView = {
          ...mapView,
          layers : {
            ...trackingMapLayers,
            enabledLayers
          },
          operationalLayers : getVisibleOperationalLayer(operationalLayers),
          id                : value
        };
        return updatedMapView;
      }
    });
    UserPreferences.set(Preference.savedMapViews, updatedMapViews, true);
    dispatch(setSavedMapViews(updatedMapViews));
  }, [layers, operationalLayers, savedMapViews]);

  const deleteSavedMapView = useCallback(({id}: SavedMapViewType, mapViews: SavedMapViewType[]) => {
    const filteredMapViews = filterSavedMapViews(id, mapViews);
    dispatch(setSavedMapViews(filteredMapViews));
  }, []);

  // Defines a utility method to set Mapview as default.
  const setDefaultMapView = useCallback((value: boolean, mapView: SavedMapViewType) => {
    let updatedMapView  = null;
    const savedMapViews = UserPreferences.get(Preference.savedMapViews);
    const updatedMapViews = savedMapViews?.map(savedMapView => {
      if (savedMapView.id !== mapView.id) {
        updatedMapView = {...savedMapView, isDefault: false};
      } else {
        updatedMapView = {...mapView, isDefault: value};
      }
      return updatedMapView;
    });
    UserPreferences.set(Preference.savedMapViews, updatedMapViews, true);
    dispatch(setSavedMapViews(updatedMapViews));
  }, []);

  const bottomSheetProps = useMemo(() => {
    return {
      mobileMapControlsOpen : mobileMapControlsOpen,
      closeMapControlsSheet : closeMapControlsSheet,
      onMapViewClick        : onMapViewClick,
      fullHeight            : true,
      selectedTool,
      onSaveMapView,
      savedMapViews,
      currentBasemap,
      toolDetailView,
      onViewAllClick,
      onGoToHomeView,
      setSavedMapView,
      onBaseMapChange,
      createOSMBasemap,
      editSavedMapView,
      setDefaultMapView,
      deleteSavedMapView
    };
  }, [
    selectedTool,
    savedMapViews,
    onSaveMapView,
    currentBasemap,
    toolDetailView,
    onViewAllClick,
    onGoToHomeView,
    onMapViewClick,
    onBaseMapChange,
    setSavedMapView,
    createOSMBasemap,
    editSavedMapView,
    setDefaultMapView,
    deleteSavedMapView,
    closeMapControlsSheet,
    mobileMapControlsOpen
  ]);
  
  return bottomSheetProps;
};

export default useBottomSheetProps;
